import React from 'react';
import Icon from '../comps/Icon';
import { getPlan, getRealPlan, getPlanPreview, setPlanPreview, clearPlanPreview, isPlanPreviewAllowed } from '../services/plan';
import { getBoost, setBoost } from '../services/boost';

/*
 * PlansPage (מנויים) — compare the subscription tiers and PREVIEW each one live, so the
 * operator can see how the app differs per plan (e.g. the visual table sketch + LIVE board
 * appear on מקצועי/רשתות but not on בסיסי). Selecting a tier sets a client-side preview
 * override and reloads so every plan-gated screen re-renders. Owner-allowlist gated (see
 * plan.js) — a real paying customer can't self-upgrade here. Restrict/replace with server-side
 * plan enforcement before onboarding paying businesses.
 */
const PLANS = [
    {
        id: 'basic', name: 'בסיסי', price: '₪99', tag: 'לעסק קטן',
        features: [
            'הזמנות אונליין מהלקוחות לפי אזור',
            'ניהול הזמנות ברשימה לפי שעות (הגיע / התיישב / לא הגיע)',
            'שולחנות ואזורים — ניהול רשימתי',
            'דוחות וחלוקת עומסים',
            'התראות SMS / מייל',
        ],
        missing: ['לוח LIVE ויזואלי', 'סקיצת מפת שולחנות', 'ריבוי סניפים'],
    },
    {
        id: 'live', name: 'מקצועי (LIVE)', price: '₪199', tag: 'הכי פופולרי',
        features: [
            'כל מה שבבסיסי',
            'לוח LIVE ויזואלי בזמן אמת',
            'סקיצת מפת שולחנות (גרירה, עריכה חזותית)',
            'הושבה חכמה',
        ],
        missing: ['ריבוי סניפים'],
    },
    {
        id: 'chain', name: 'רשתות', price: '₪699', tag: 'לרשתות',
        features: [
            'כל מה שבמקצועי',
            'ריבוי סניפים ללא הגבלה',
            'ניהול מרוכז של כל הסניפים',
        ],
        missing: [],
    },
];

// ── BOOST (קידום העסק) — v1 MANUAL/owner activation (no payment gateway yet), priced by DAYS.
// Shows current status (getBoost) and lets the owner start a boost (7/14/30 days) or cancel it
// (setBoost(0)). Server-side: setBusinessBoost is authenticated + scoped to the session business.
function formatBoostDate(dt) {
    if (!dt) return '';
    try {
        // Backend returns a MySQL datetime "YYYY-MM-DD HH:MM:SS"; render just the date, he-IL.
        const d = new Date(String(dt).replace(' ', 'T'));
        if (isNaN(d.getTime())) return String(dt);
        return d.toLocaleDateString('he-IL', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch (e) { return String(dt); }
}

function BoostPanel() {
    const [status, setStatus] = React.useState({ boostedUntil: null, active: false });
    const [busy, setBusy] = React.useState(false);
    const [loading, setLoading] = React.useState(true);

    React.useEffect(() => {
        let alive = true;
        getBoost().then((s) => { if (alive) { setStatus(s); setLoading(false); } });
        return () => { alive = false; };
    }, []);

    const apply = async (days) => {
        if (busy) return;
        setBusy(true);
        const s = await setBoost(days);
        setStatus(s);
        setBusy(false);
    };

    const chipBase = {
        minHeight: 44, padding: '0 var(--s-4)', borderRadius: 'var(--r-md)',
        border: '1px solid var(--brand)', background: 'var(--surface)', color: 'var(--brand-strong)',
        fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', fontSize: 'var(--fs-sm)',
        cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
    };

    return (
        <div style={{ marginTop: 'var(--s-6)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 'var(--s-5)', boxShadow: 'var(--shadow-sm)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, background: 'var(--brand-soft)', color: 'var(--brand)', borderRadius: 'var(--r-sm)' }}>
                    <Icon name="tag" size={18} />
                </span>
                <h2 style={{ margin: 0, fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)', color: 'var(--text)' }}>קידום העסק (BOOST)</h2>
            </div>
            <p style={{ margin: '8px 0 0', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
                עסק מקודם מדורג <b>ראשון</b> בעמוד גילוי העסקים ומקבל תג "מקודם".
            </p>

            <div style={{ marginTop: 'var(--s-4)', padding: 'var(--s-4)', background: 'var(--bg-subtle)', border: '1px solid var(--border)', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', color: 'var(--text)' }}>
                {loading ? 'טוען סטטוס…'
                    : status.active
                        ? <>העסק <b style={{ color: 'var(--brand-strong)' }}>מקודם</b> עד {formatBoostDate(status.boostedUntil)}</>
                        : <>העסק <b>לא מקודם</b> כרגע</>}
            </div>

            <div style={{ marginTop: 'var(--s-4)', display: 'flex', flexWrap: 'wrap', gap: 'var(--s-2)' }}>
                <button type="button" disabled={busy} onClick={() => apply(7)} style={chipBase}>7 ימים</button>
                <button type="button" disabled={busy} onClick={() => apply(14)} style={chipBase}>14 ימים</button>
                <button type="button" disabled={busy} onClick={() => apply(30)} style={chipBase}>30 ימים</button>
                <button type="button" disabled={busy} onClick={() => apply(0)}
                    style={{ ...chipBase, border: '1px solid var(--border-strong)', color: 'var(--text-muted)' }}>
                    בטל קידום
                </button>
            </div>

            <p style={{ margin: 'var(--s-4) 0 0', color: 'var(--text-faint)', fontSize: 'var(--fs-xs)' }}>
                גרסה ראשונית — הפעלה ידנית ללא חיוב; תמחור לפי ימים בהמשך.
            </p>
        </div>
    );
}

export default function PlansPage() {
    const allowed = isPlanPreviewAllowed();
    const current = getPlan();
    const real = getRealPlan();
    const isPreview = !!getPlanPreview();

    const pick = (id) => { if (id !== current) { setPlanPreview(id); window.location.reload(); } };
    const reset = () => {
        clearPlanPreview();
        try { const u = new URL(window.location.href); u.searchParams.delete('plan'); window.history.replaceState({}, '', u.pathname + u.search + u.hash); } catch (e) { /* ignore */ }
        window.location.reload();
    };

    return (
        <div className="plans-page" style={{ direction: 'rtl', fontFamily: 'var(--font)', padding: 'var(--s-5)' }}>
            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--s-3)' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: 'var(--fs-2xl)', fontWeight: 'var(--fw-bold)', color: 'var(--text)' }}>מנויים</h1>
                    <p style={{ margin: '4px 0 0', color: 'var(--text-muted)', fontSize: 'var(--fs-sm)' }}>
                        השוו בין החבילות — ובחרו חבילה כדי לראות איך המערכת נראית בה (למשל סקיצת השולחנות מופיעה במקצועי ומעלה).
                    </p>
                </div>
                {allowed && isPreview && (
                    <button type="button" onClick={reset}
                        style={{ border: '1px solid var(--brand)', background: 'var(--brand-soft)', color: 'var(--brand-strong)', borderRadius: 'var(--r-pill)', padding: '8px 16px', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: 'pointer', minHeight: 40 }}>
                        חזרה למנוי האמיתי{real ? ` (${PLANS.find((p) => p.id === real)?.name || real})` : ''}
                    </button>
                )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 'var(--s-4)', marginTop: 'var(--s-5)' }}>
                {PLANS.map((p) => {
                    const active = current === p.id;
                    return (
                        <div key={p.id}
                            style={{ background: 'var(--surface)', border: active ? '2px solid var(--brand)' : '1px solid var(--border)', borderRadius: 'var(--r-lg)', padding: 'var(--s-5)', boxShadow: active ? 'var(--shadow-md)' : 'var(--shadow-sm)', display: 'flex', flexDirection: 'column' }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                <span style={{ fontSize: 'var(--fs-lg)', fontWeight: 'var(--fw-bold)', color: 'var(--text)' }}>{p.name}</span>
                                <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--brand-strong)', background: 'var(--brand-soft)', borderRadius: 'var(--r-pill)', padding: '2px 10px', fontWeight: 'var(--fw-bold)' }}>{p.tag}</span>
                            </div>
                            <div style={{ margin: '8px 0 12px', fontSize: 'var(--fs-2xl)', fontWeight: 'var(--fw-bold)', color: 'var(--brand)' }}>{p.price}<span style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-muted)', fontWeight: 'var(--fw-medium)' }}> / חודש</span></div>
                            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                                {p.features.map((f) => (
                                    <li key={f} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 'var(--fs-sm)', color: 'var(--text)' }}>
                                        <Icon name="check" size={16} style={{ color: 'var(--success)', flexShrink: 0, marginTop: 2 }} /> {f}
                                    </li>
                                ))}
                                {p.missing.map((f) => (
                                    <li key={f} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 'var(--fs-sm)', color: 'var(--text-faint)', textDecoration: 'line-through' }}>
                                        <Icon name="x" size={16} style={{ color: 'var(--text-faint)', flexShrink: 0, marginTop: 2 }} /> {f}
                                    </li>
                                ))}
                            </ul>
                            {allowed ? (
                                <button type="button" onClick={() => pick(p.id)} disabled={active}
                                    style={{ marginTop: 'var(--s-4)', minHeight: 44, borderRadius: 'var(--r-md)', border: 'none', fontWeight: 'var(--fw-bold)', fontFamily: 'var(--font)', cursor: active ? 'default' : 'pointer', background: active ? 'var(--bg-subtle)' : 'var(--brand)', color: active ? 'var(--text-muted)' : 'var(--on-brand)' }}>
                                    {active ? 'המנוי הנוכח בתצוגה ✓' : 'צפה במנוי זה'}
                                </button>
                            ) : (
                                active && <div style={{ marginTop: 'var(--s-4)', textAlign: 'center', color: 'var(--brand-strong)', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}>המנוי שלך</div>
                            )}
                        </div>
                    );
                })}
            </div>

            <BoostPanel />
        </div>
    );
}
